/* eslint-disable no-underscore-dangle */
import React from 'react';
import { posthog } from 'posthog-js';
import { IProps } from './types';
import ErrorBoundaryUI from '../../error-boundary/error-boundary';
import store from '../../../store';
import { setShowDialer } from '../../../components/dialer/dialer-slice';
import {
  capturePosthogException,
  generateCrashReportId,
} from '../../utils/posthog-error-reporting';
import { ErrorBoundaryState } from '../crash-report-detail-form/types';

/** One auto-reload per tab session for DOM mismatch — avoids infinite reload loops. */
const DOM_MISMATCH_RELOAD_SESSION_KEY =
  'sh-error-boundary-dom-mismatch-did-reload';

/**
 * React #418 / DOMException when the virtual DOM and real DOM are out of sync
 * (common after concurrent updates, portals, or third-party script DOM changes).
 */
const isReactDomTreeMismatchError = (error: Error): boolean => {
  const { name, message } = error;
  const isNotFoundName =
    name === 'NotFoundError' || message?.includes('NotFoundError');
  if (!isNotFoundName) {
    return false;
  }
  return message?.includes('insertBefore') || message?.includes('removeChild');
};

class PosthogErrorBoundary extends React.Component<IProps, ErrorBoundaryState> {
  // eslint-disable-next-line react/state-in-constructor
  state: ErrorBoundaryState = {
    error: null,
    componentStack: '',
    crashReportId: null,
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    posthog.captureException(error, {
      component_stack: errorInfo.componentStack,
    });
    const crashReportId = generateCrashReportId();

    store.dispatch(setShowDialer(false));

    capturePosthogException(error, {
      productionOnly: false,
      tags: {
        isFallbackUIShown: 'true',
      },
      componentStack: errorInfo.componentStack,
      fingerprint: crashReportId,
    });

    if ((posthog as any).__loaded) {
      posthog.capture('broken_screen_shown', {
        type: 'Exception',
        error_message: error.message,
        component_stack: errorInfo.componentStack,
        crash_report_id: crashReportId,
      });
    }

    if (isReactDomTreeMismatchError(error)) {
      try {
        if (window.sessionStorage.getItem(DOM_MISMATCH_RELOAD_SESSION_KEY)) {
          this.setState({
            componentStack: errorInfo.componentStack,
            crashReportId,
          });
          return;
        }
        window.sessionStorage.setItem(DOM_MISMATCH_RELOAD_SESSION_KEY, '1');
      } catch {
        // sessionStorage unavailable (e.g. private mode) — still attempt recovery
      }
      window.location.reload();
      return;
    }

    this.setState({
      componentStack: errorInfo.componentStack,
      crashReportId,
    });
  }

  render(): React.ReactNode {
    const { error, componentStack, crashReportId } = this.state;
    const { children } = this.props;

    if (error) {
      return (
        <ErrorBoundaryUI
          crashReportId={crashReportId}
          errorMessage={error.message}
          componentStack={componentStack}
        />
      );
    }

    return children;
  }
}

const ErrorBoundaryWrapper: React.FC<IProps> = ({ children }) => (
  <PosthogErrorBoundary>{children}</PosthogErrorBoundary>
);

export default ErrorBoundaryWrapper;
